import { useState } from 'react';
import {
  useGetScimGroupMappings,
  useUpdateScimGroupMapping,
} from '@tinct/core-api';
import { MemberRole, ReadScimGroupMapping } from '@tinct/core-api-models';
import {
  Alert,
  AlertDescription,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tinct/web-components';
import { TriangleAlertIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MemberRoleSelect } from '../../../components/member-role-select';
import { HELP_ARTICLES } from '../help-articles';
import { MapGroupToAdminModal } from '../modals/map-group-to-admin-modal';
import { SECURITY_CARD_ANCHORS } from '../security-card-anchors';
import { CardAnchorLink } from './card-anchor-link';

export type ScimGroupMappingsProps = {
  organizationId: string;
};

/**
 * The groups the directory pushed and the role each maps to, inside the SCIM card once provisioning
 * is on. Mapping to Admin goes through a confirmation; `-` deletes the mapping.
 */
export function ScimGroupMappings({ organizationId }: ScimGroupMappingsProps) {
  // The group the Admin confirmation is about, `null` while it is closed
  const [groupToAdmin, setGroupToAdmin] = useState<ReadScimGroupMapping | null>(null);

  const { data: groups, isLoading, error } = useGetScimGroupMappings({ organizationId });

  const updateMapping = useUpdateScimGroupMapping({
    onSuccess: (_, { role }) => {
      toast.success(role ? 'Group mapping saved' : 'Group mapping removed');
    },
    // The API is the gate: a change that would leave the workspace without an admin (409, 5704)
    // comes back with a message that says so. The select keeps showing the stored role.
    onError: (apiError) => toast.error(apiError.error),
  });

  const changeRole = (group: ReadScimGroupMapping, role: MemberRole | null) => {
    if (!group.groupId || role === group.role) return;
    if (role === 'admin') {
      setGroupToAdmin(group);
      return;
    }
    updateMapping.mutate({ organizationId, groupId: group.groupId, role });
  };

  const pendingGroupId = updateMapping.isPending
    ? updateMapping.variables?.groupId
    : undefined;

  return (
    <div className="flex flex-col gap-3 border-t pt-6">
      <div className="flex flex-col gap-1">
        <span className="font-semibold">Directory groups</span>
        <span className="text-sm text-muted-foreground">
          Map the groups of your directory to a role. A member of several mapped
          groups gets the highest of their roles; a member who leaves their last
          mapped group gets the role of new members set in{' '}
          <CardAnchorLink anchor={SECURITY_CARD_ANCHORS.ssoProvisioning}>
            Provisioning
          </CardAnchorLink>
          . Members of no mapped group keep the role given to them in Tinct.
          Groups have no other effect in Tinct.
        </span>
      </div>

      {error ? (
        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertDescription>
            The directory groups could not be loaded. {error.error}
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <Skeleton className="h-20 w-full rounded-xl" />
      ) : !groups?.length ? (
        <p className="text-sm text-muted-foreground">
          Your directory has not pushed any group yet. In Okta, add the groups
          under <span className="font-medium">Push Groups</span> of the Tinct
          application; in Microsoft Entra ID, assign them to the enterprise
          application, and the next provisioning cycle pushes them.{' '}
          <a
            href={HELP_ARTICLES.scimGroupMapping}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4"
          >
            How to push groups
          </a>
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Group</TableHead>
              <TableHead>Members</TableHead>
              <TableHead className="text-right">Role</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.groupId}>
                <TableCell className="font-medium">
                  {group.displayName ?? group.externalId ?? '-'}
                </TableCell>
                <TableCell>{group.memberCount ?? 0}</TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <MemberRoleSelect
                      allowNone
                      value={group.role}
                      onValueChange={(role) => changeRole(group, role)}
                      disabled={pendingGroupId === group.groupId}
                      aria-label={`Role of ${group.displayName ?? 'the group'}`}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {groupToAdmin && (
        <MapGroupToAdminModal
          open
          onOpenChange={(open) => !open && setGroupToAdmin(null)}
          organizationId={organizationId}
          group={groupToAdmin}
        />
      )}
    </div>
  );
}
